import { useState } from 'react';
import { ENTITY_TYPES, RULE_EFFECTS } from '../../../@types/rules';
import { RuleVisualization } from '../Visualization';

function initialFormState(rule) {
    return {
        name: rule?.name ?? '',
        firstEntity: rule?.definition.firstEntity.entityType ?? '',
        secondEntity: rule?.definition.secondEntity?.entityType ?? '',
        effect: rule?.effect ?? '',
        touchingEnabled: rule?.definition.topologies.includes('Touching') ?? true,
        overlapToleranceCm3: rule?.definition.overlapToleranceCm3?.toString() ?? '',
        minClearanceCm: rule?.definition.minClearanceCm?.toString() ?? '',
    };
}

function parseOptionalNumber(value) {
    if (value.trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : Number.NaN;
}

export function validateInterferenceRuleForm(values) {
    const errors = {};
    const name = values.name.trim();
    const overlapToleranceCm3 = parseOptionalNumber(values.overlapToleranceCm3);
    const minClearanceCm = parseOptionalNumber(values.minClearanceCm);

    if (!name) errors.name = 'Enter a rule name.';
    else if (name.length > 120) errors.name = 'Rule name cannot exceed 120 characters.';

    if (!values.firstEntity) errors.firstEntity = 'Select the first entity.';
    if (!values.secondEntity) errors.secondEntity = 'Select the second entity.';
    if (!values.effect) errors.effect = 'Select an effect.';

    if (!values.touchingEnabled && values.minClearanceCm.trim() === '') {
        errors.detection = 'Enable Touching or enter a minimum clearance.';
    }

    if (!values.touchingEnabled && values.overlapToleranceCm3.trim() !== '') {
        errors.overlapToleranceCm3 = 'Overlap tolerance requires Touching detection.';
    } else if (Number.isNaN(overlapToleranceCm3)) {
        errors.overlapToleranceCm3 = 'Enter a valid overlap tolerance.';
    } else if (overlapToleranceCm3 !== null && overlapToleranceCm3 < 0) {
        errors.overlapToleranceCm3 = 'Overlap tolerance cannot be negative.';
    }

    if (Number.isNaN(minClearanceCm)) {
        errors.minClearanceCm = 'Enter a valid minimum clearance.';
    } else if (minClearanceCm !== null && minClearanceCm < 0) {
        errors.minClearanceCm = 'Minimum clearance cannot be negative.';
    }

    return {
        errors,
        input: Object.keys(errors).length === 0 ? {
            name,
            effect: values.effect,
            definition: {
                type: 'Interference',
                firstEntity: { entityType: values.firstEntity },
                secondEntity: { entityType: values.secondEntity },
                topologies: values.touchingEnabled ? ['Touching'] : [],
                overlapToleranceCm3,
                minClearanceCm,
            },
        } : null,
    };
}

function FieldError({ id, children }) {
    if (!children) return null;
    return <p id={id} className="rules-field-error" role="alert">{children}</p>;
}

export const InterferenceRuleForm = ({
    mode = 'create',
    initialRule = null,
    onSubmit,
    onCancel,
    isSubmitting,
    serverError,
    sessionExpired = false,
    onSignInAgain,
}) => {
    const [values, setValues] = useState(() => initialFormState(initialRule));
    const [errors, setErrors] = useState({});
    const isEditing = mode === 'edit';

    const updateValue = (field, value) => {
        setValues((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: undefined, detection: undefined }));
    };

    const updateTouching = (enabled) => {
        setValues((current) => ({
            ...current,
            touchingEnabled: enabled,
            overlapToleranceCm3: enabled ? current.overlapToleranceCm3 : '',
        }));
        setErrors((current) => ({
            ...current,
            touchingEnabled: undefined,
            overlapToleranceCm3: undefined,
            detection: undefined,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const validation = validateInterferenceRuleForm(values);
        setErrors(validation.errors);
        if (!validation.input) return;
        onSubmit({ input: validation.input });
    };

    return (
        <div className="zrf-container irf-editor-container">
            <button type="button" className="zrf-back" onClick={onCancel} disabled={isSubmitting}>
                &larr; Back to Rules
            </button>

            <div className="zrf-page-header">
                <h1 className="zrf-page-title">{isEditing ? 'Edit Interference Rule' : 'Create Interference Rule'}</h1>
                <p className="zrf-page-subtitle">
                    Detect unwanted contact or insufficient clearance between two BIM entity types.
                </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
                {serverError && (
                    <div className="rules-alert rules-alert-error" role="alert">
                        <span>{serverError}</span>
                        {sessionExpired && onSignInAgain && (
                            <button type="button" className="rules-btn-ghost" onClick={onSignInAgain}>Sign in again</button>
                        )}
                    </div>
                )}

                <div className="irf-editor-workspace">
                    <div className="irf-form-column">
                        <div className="zrf-section">
                            <label className="zrf-label" htmlFor="interference-rule-name">Rule name</label>
                            <input
                                id="interference-rule-name"
                                className="zrf-input"
                                value={values.name}
                                onChange={(event) => updateValue('name', event.target.value)}
                                maxLength={121}
                                aria-invalid={Boolean(errors.name)}
                                aria-describedby={errors.name ? 'interference-rule-name-error' : undefined}
                                placeholder="e.g. Duct must not touch wall"
                                autoFocus
                            />
                            <FieldError id="interference-rule-name-error">{errors.name}</FieldError>
                        </div>

                        <div className="zrf-section">
                            <div className="rules-form-grid">
                                <div>
                                    <label className="zrf-label" htmlFor="interference-first-entity">First entity</label>
                                    <select
                                        id="interference-first-entity"
                                        className="zrf-select zrf-select-full"
                                        value={values.firstEntity}
                                        onChange={(event) => updateValue('firstEntity', event.target.value)}
                                        aria-invalid={Boolean(errors.firstEntity)}
                                    >
                                        <option value="">Select an entity</option>
                                        {ENTITY_TYPES.map((entityType) => <option key={entityType} value={entityType}>{entityType}</option>)}
                                    </select>
                                    <FieldError>{errors.firstEntity}</FieldError>
                                </div>
                                <div>
                                    <label className="zrf-label" htmlFor="interference-second-entity">Second entity</label>
                                    <select
                                        id="interference-second-entity"
                                        className="zrf-select zrf-select-full"
                                        value={values.secondEntity}
                                        onChange={(event) => updateValue('secondEntity', event.target.value)}
                                        aria-invalid={Boolean(errors.secondEntity)}
                                    >
                                        <option value="">Select an entity</option>
                                        {ENTITY_TYPES.map((entityType) => <option key={entityType} value={entityType}>{entityType}</option>)}
                                    </select>
                                    <FieldError>{errors.secondEntity}</FieldError>
                                </div>
                            </div>
                        </div>

                        <div className="zrf-section">
                            <label className="zrf-label" htmlFor="interference-effect">Effect</label>
                            <select
                                id="interference-effect"
                                className="zrf-select zrf-select-full"
                                value={values.effect}
                                onChange={(event) => updateValue('effect', event.target.value)}
                                aria-invalid={Boolean(errors.effect)}
                            >
                                <option value="">Select an effect</option>
                                {RULE_EFFECTS.map((effect) => <option key={effect} value={effect}>{effect}</option>)}
                            </select>
                            <FieldError>{errors.effect}</FieldError>
                        </div>

                        <fieldset className="zrf-section rules-detection-fieldset">
                            <legend className="zrf-label">Detection</legend>
                            <label className="rules-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={values.touchingEnabled}
                                    onChange={(event) => updateTouching(event.target.checked)}
                                />
                                <span>
                                    <strong>Touching</strong>
                                    <small>Detect physical contact or overlap between the selected entities.</small>
                                </span>
                            </label>
                            <FieldError>{errors.detection}</FieldError>

                            <div className="rules-form-grid rules-numeric-grid">
                                <div>
                                    <label className="zrf-label" htmlFor="interference-overlap-tolerance">Overlap tolerance (cm³)</label>
                                    <input
                                        id="interference-overlap-tolerance"
                                        type="number"
                                        min="0"
                                        step="any"
                                        className="zrf-input"
                                        value={values.overlapToleranceCm3}
                                        onChange={(event) => updateValue('overlapToleranceCm3', event.target.value)}
                                        disabled={!values.touchingEnabled}
                                        aria-invalid={Boolean(errors.overlapToleranceCm3)}
                                        placeholder="Optional"
                                    />
                                    <FieldError>{errors.overlapToleranceCm3}</FieldError>
                                </div>
                                <div>
                                    <label className="zrf-label" htmlFor="interference-min-clearance">Minimum clearance (cm)</label>
                                    <input
                                        id="interference-min-clearance"
                                        type="number"
                                        min="0"
                                        step="any"
                                        className="zrf-input"
                                        value={values.minClearanceCm}
                                        onChange={(event) => updateValue('minClearanceCm', event.target.value)}
                                        aria-invalid={Boolean(errors.minClearanceCm)}
                                        placeholder="Optional"
                                    />
                                    <FieldError>{errors.minClearanceCm}</FieldError>
                                </div>
                            </div>
                        </fieldset>

                        <div className="zrf-actions">
                            <button type="submit" className="rules-btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? (isEditing ? 'Saving…' : 'Creating…') : (isEditing ? 'Save Changes' : 'Create Rule')}
                            </button>
                            <button type="button" className="rules-btn-ghost" onClick={onCancel} disabled={isSubmitting}>
                                Cancel
                            </button>
                        </div>
                    </div>

                    <RuleVisualization
                        ruleKind="interference"
                        draft={{
                            firstEntity: values.firstEntity,
                            secondEntity: values.secondEntity,
                            touchingEnabled: values.touchingEnabled,
                            overlapToleranceCm3: values.overlapToleranceCm3,
                            minClearanceCm: values.minClearanceCm,
                        }}
                    />
                </div>
            </form>
        </div>
    );
};
